import _ from 'lodash'
import { validators } from '@blazar/tracking'
import gtm from '@blazar/tracking-google-tagmanager'

import * as accountActions from 'reduxBits/account/actions'
import * as phoneNumberActions from 'reduxBits/phoneNumber/actions'
import * as classifiedActions from 'reduxBits/classified/actions'

import { renderDataLayerObject } from 'tracking/dataLayerUtils'
import { errorTypes } from 'reduxBits/account'

const createDataLayerObject = (state, localData = {}) =>
  renderDataLayerObject({
    // No globals needed
    ...{
      objectCustom: undefined,
      event: 'event',
      eventType: 'Engagement',
      objectType: 'UIElement',
    },
    ...localData,
  })

export const saveAd = {
  validators: [validators.redux(accountActions.RECEIVE_CREATE_SAVED_AD)],
  events: [
    gtm.event(({ state }) => {
      const hasReachedSavedAdLimit =
        _.get(state, ['account', 'savedAds', 'errorType']) === errorTypes.MAX_SAVED_ADS_ERROR
      return createDataLayerObject(state, {
        objectId: _.get(state, 'classified.requestedAd.ad.listId', ''),
        objectName: 'Save ad',
        eventType: 'Save',
        eventCategory: 'save',
        eventAction: 'saved ad',
        objectType: 'ClassifiedAd',
        ...(hasReachedSavedAdLimit && {
          objectType: 'Error',
          eventType: 'Engagement',
          eventAction: 'too many saved ads',
          objectName: 'Error save ad',
          objectCause: [{ definition: errorTypes.MAX_SAVED_ADS_ERROR }],
        }),
      })
    }),
  ],
}

export const unsaveAd = {
  validators: [validators.redux(accountActions.RECEIVE_DELETE_SAVED_AD)],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: _.get(state, 'classified.requestedAd.ad.listId', ''),
        objectName: 'Unsave ad',
        eventType: 'Unsave',
        eventCategory: 'save',
        eventAction: 'unsaved ad',
        objectType: 'ClassifiedAd',
      }),
    ),
  ],
}

export const viewPhoneNumber = {
  validators: [validators.redux(phoneNumberActions.RECEIVE_VIEW_PHONE_NUMBER)],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: _.get(state, 'classified.requestedAd.ad.listId', ''),
        objectName: 'Ad phone number showed',
        eventType: 'Show',
        eventCategory: 'lead',
        eventAction: 'show phone number',
        objectType: 'PhoneContact',
        intent: 'Create',
      }),
    ),
  ],
}

export const calledPhoneNumber = {
  validators: [validators.redux(phoneNumberActions.CALLED_NUMBER)],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: _.get(state, 'classified.requestedAd.ad.listId', ''),
        objectName: 'Ad phone number called',
        eventType: 'Create',
        objectType: 'PhoneContact',
        eventCategory: 'lead',
        eventAction: 'call phone number',
      }),
    ),
  ],
}

export const contractOfSaleClicked = {
  validators: [
    validators.redux(
      classifiedActions.CONTRACT_OF_SALE_CLICKED,
      () => true,
      ({ payload }) => payload,
    ),
  ],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: 'contract_clicked',
        objectName: 'Contract of sale clicked',
        objectType: 'UIElement',
        intent: 'View',
        eventType: 'Engagement',
      }),
    ),
  ],
}

export const blocketPaymentButtonClicked = {
  validators: [
    validators.redux(
      classifiedActions.BLOCKET_PAYMENT_BUTTON_CLICKED,
      () => true,
      ({ payload }) => payload,
    ),
  ],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: 'blocket_payment_button_clicked',
        objectName: 'Blocket payment button clicked',
        eventAction: 'blocket payment button',
        objectType: 'UIElement',
        eventType: 'Engagement',
        eventCategory: 'engagement',
      }),
    ),
  ],
}

export const blocketPaymentBadgeClicked = {
  validators: [
    validators.redux(
      classifiedActions.BLOCKET_PAYMENT_BADGE_CLICKED,
      () => true,
      ({ payload }) => payload,
    ),
  ],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: 'blocket_payment_badge_clicked',
        objectName: 'Blocket payment badge clicked',
        eventAction: 'blocket payment badge',
        objectType: 'UIElement',
        eventType: 'Engagement',
        eventCategory: 'engagement',
      }),
    ),
  ],
}

export const blocketPaymentCreditSpaceClicked = {
  validators: [
    validators.redux(
      classifiedActions.BLOCKET_PAYMENT_CREDITSPACE_CLICKED,
      () => true,
      ({ payload }) => payload,
    ),
  ],
  events: [
    gtm.event(({ state }) =>
      createDataLayerObject(state, {
        objectId: 'blocket_payment_creditspace_clicked',
        objectName: 'Blocket payment creditspace clicked',
        eventAction: 'blocket payment creditspace',
        objectType: 'UIElement',
        eventType: 'Engagement',
        eventCategory: 'engagement',
      }),
    ),
  ],
}

export const infoPageLinkClicked = {
  validators: [
    validators.redux(
      classifiedActions.INFO_PAGE_LINK_CLICKED,
      () => true,
      ({ payload }) => payload,
    ),
  ],
  events: [
    gtm.event(({ state }) => {
      const ad = _.get(state, 'classified.requestedAd.ad', {})
      const advertiserType = _.get(ad, 'advertiser.type')
      const storeId = _.get(ad, 'store.storeId')
      const storeName = _.get(ad, 'store.name')
      const externalLink = _.get(ad, 'infopage.url')

      return createDataLayerObject(state, {
        objectId: `external-link-read-more-${advertiserType}-site`,
        eventCategory: 'external link',
        eventAction: `clicked go to ${advertiserType} site`,
        eventLabel: `${storeId} - ${storeName}`,
        target: {
          id: 'external',
          type: 'Page',
          name: storeName,
          url: externalLink,
        },
      })
    }),
  ],
}
